/**
 * Utility functions for tracking analytics events
 */

// Define types for analytics events
type EventType = 'page_view' | 'scroll_depth' | 'click' | 'conversion' | 'engagement';

interface TrackingOptions {
  page?: string;
  [key: string]: any;
}

/**
 * Track an analytics event
 * @param eventType The type of event
 * @param eventName The name of the event
 * @param options Additional tracking options
 */
export const trackEvent = (
  eventType: EventType,
  eventName: string,
  options: TrackingOptions = {}
): void => {
  try {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, {
        event_category: eventType,
        ...options,
      });
    }
  } catch (e) {
    console.error(`Failed to track ${eventType} event "${eventName}":`, e);
  }
};

/**
 * Track a page view
 * @param pageName The name of the page
 * @param path The path of the page
 * @param options Additional tracking options
 */
export const trackPageView = (
  pageName: string,
  path: string,
  options: TrackingOptions = {}
): void => {
  trackEvent('page_view', 'page_view', {
    page_title: pageName,
    page_path: path,
    ...options,
  });
};

/**
 * Track a conversion event
 * @param eventName The name of the conversion event
 * @param options Additional tracking options
 */
export const trackConversion = (
  eventName: string,
  options: TrackingOptions = {}
): void => {
  trackEvent('conversion', eventName, options);
};

/**
 * Track an engagement event
 * @param eventName The name of the engagement event
 * @param options Additional tracking options
 */
export const trackEngagement = (
  eventName: string,
  options: TrackingOptions = {}
): void => {
  trackEvent('engagement', eventName, options);
};

/**
 * Track scroll depth
 * @param depth The scroll depth percentage
 * @param options Additional tracking options
 */
export const trackScrollDepth = (
  depth: number,
  options: TrackingOptions = {}
): void => {
  trackEvent('scroll_depth', 'scroll_depth', {
    depth,
    ...options,
  });
};

/**
 * Set up scroll depth tracking
 * @param pageName The name of the page
 * @param options Additional tracking options
 * @returns A cleanup function to remove the scroll listener
 */
export const setupScrollTracking = (
  pageName: string,
  options: TrackingOptions = {}
): (() => void) => {
  // Track scroll depth
  const handleScroll = () => {
    const scrollDepth = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
    
    if (scrollDepth === 25 || scrollDepth === 50 || scrollDepth === 75 || scrollDepth === 100) {
      trackScrollDepth(scrollDepth, { page: pageName, ...options });
    }
  };
  
  window.addEventListener('scroll', handleScroll);
  return () => window.removeEventListener('scroll', handleScroll);
};

// Define window.gtag for TypeScript
declare global {
  interface Window {
    gtag: (command: string, action: string, params?: any) => void;
  }
} 